import { NO_PROJECT_LABEL } from '@shared/palette';
import { Plus } from 'lucide-react';
import { ProjectColorBar } from '@/components/ColorSwatch';
import { EmptyState } from '@/components/EmptyState';
import { SectionTitle } from '@/components/SectionTitle';
import { Button } from '@/components/ui/button';
import { recentEntries } from '@/lib/dashboard-model';
import { formatDurationHm, formatShortLocalDate } from '@/lib/format';
import type { EntryListItem } from '@/types/entries';

interface RecentEntriesProps {
  items: EntryListItem[];
  onEdit: (entry: EntryListItem) => void;
  /** Sprint 15 (§9.3): absent on a shift project, where Log time is not offered. */
  onLogTime?: () => void;
}

export function RecentEntries({ items, onEdit, onLogTime }: RecentEntriesProps): JSX.Element {
  const recent = recentEntries(items);
  return (
    <section aria-labelledby="recent-heading">
      <SectionTitle id="recent-heading" title="Recent entries" hint="Your most recent time entries from the last 7 days. Click one to edit it." />
      {recent.length === 0 ? (
        <EmptyState
          title="No entries in the last 7 days"
          action={
            onLogTime ? (
              <Button onClick={onLogTime}>
                <Plus /> Log time
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="card space-y-0.5 p-2">
          {recent.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => onEdit(e)}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ProjectColorBar hex={e.project?.color} />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs text-muted-foreground">{e.project?.name ?? NO_PROJECT_LABEL}</span>
                  {e.description && <span className="mt-0.5 block truncate text-xs text-foreground">{e.description}</span>}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">{formatShortLocalDate(e.date)}</span>
                <span className="shrink-0 font-mono text-sm tabular-nums text-foreground">{formatDurationHm(e.durationSeconds)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
